import { useEffect, useMemo, useState, Suspense } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { getTinyAccountById } from 'store/account';

import {
  ActivationNavigation,
  useActivationNavigation,
} from './ActivationNavigation';
import { Kit } from '@repo/types';
import { ActivationStepKey } from 'hooks/useActivationSteps';
import TinyAccount from 'types/TinyAccount';
import useTinyAccounts from 'services/account/useTinyAccounts';
import {
  ActivationFooterContext,
  ActivationScreen,
  ActivationStepper,
  createActivationFooterNode,
  ExitFormsWarningModal,
  Spinfinity,
} from 'components';
import { getFeature } from '@repo/features';
import useFeature from 'services/features/useFeature';
import useAllFeatures from 'services/features/useAllFeatures';

import styles from './Kits.module.css';

export type KitActivationState = {
  kit: Partial<Kit>;
  tinyAccount: { id: string } & Partial<TinyAccount>;
};

export type KitStepProps = {
  tinyAccounts: TinyAccount[];
  step: number;
  setCurrentActivationStep: (step: number) => void;
  activationState: KitActivationState;
  setActivationState: (state: KitActivationState) => void;
};

export type KitStep = {
  id: string;
  Component: React.FC<KitStepProps>;
  exactPath?: boolean;
  optional?: boolean;
  stage?: ActivationStepKey;
  /** Only shown behind the redesigned_kit_activation_flow flag (e.g. a step new in ENG-4942). */
  redesignOnly?: boolean;
  /** Only shown when this Optimizely flag is enabled for the account. */
  featureFlag?: string;
  /** Drop this step entirely for a given activation state (e.g. partner kits skip the tutorial). */
  hideIf?: (state: KitActivationState) => boolean;
};

interface ActivateKitProps {
  readonly steps: readonly KitStep[];
}

export const ActivateKit: React.FC<ActivateKitProps> = ({ steps }) => {
  const { tinyAccounts } = useTinyAccounts();
  const navigate = useNavigate();
  const [isExitModalOpen, setIsExitModalOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const tinyaccount_id = searchParams.get('tinyaccount_id');
  const tinyAccount = useSelector(getTinyAccountById(tinyaccount_id ?? ''));

  const [currentActivationStep, setCurrentActivationStep] = useState(
    tinyaccount_id ? 1 : 0,
  );

  const [activationState, setActivationState] = useState<KitActivationState>({
    tinyAccount: tinyAccount ? tinyAccount : { id: '' },
    kit: {},
  });
  useEffect(() => {
    if (tinyAccount && !activationState.tinyAccount.id) {
      setActivationState({
        ...activationState,
        tinyAccount,
      });
      if (currentActivationStep === 0) setCurrentActivationStep(1);
    }
  }, [tinyAccount, activationState, currentActivationStep]);

  const { isFeatureEnabled: isRedesignedActivationEnabled } = useFeature(
    'redesigned_kit_activation_flow',
  );
  const { data: features } = useAllFeatures();
  const visibleSteps = useMemo(
    () =>
      steps.filter(
        s =>
          (!s.redesignOnly || isRedesignedActivationEnabled) &&
          (!s.featureFlag ||
            getFeature(features, s.featureFlag).isFeatureEnabled) &&
          !s.hideIf?.(activationState),
      ),
    [steps, activationState, isRedesignedActivationEnabled, features],
  );

  const StepComponent = visibleSteps[currentActivationStep]?.Component;

  const [footerNode] = useState(createActivationFooterNode);
  const navigation = useActivationNavigation({
    step: currentActivationStep,
    startingStep: tinyAccount ? 1 : 0,
    steps: visibleSteps,
    setCurrentActivationStep,
    kitId: activationState.kit.id,
    kit: activationState.kit,
  });

  const stepElement = tinyAccounts && StepComponent && (
    <Suspense fallback={<Spinfinity />}>
      <StepComponent
        key={visibleSteps[currentActivationStep]?.id}
        tinyAccounts={tinyAccounts}
        step={currentActivationStep}
        setCurrentActivationStep={setCurrentActivationStep}
        activationState={activationState}
        setActivationState={setActivationState}
      />
    </Suspense>
  );

  // Kits with a 'skip' survey config have no health forms step to warn about.
  const hasHealthForms = navigation.activationSteps.some(
    s => s.key === 'forms',
  );
  const goHome = () =>
    navigate('/', {
      state: { tinyAccountId: activationState.tinyAccount.id },
    });

  // Steps put their primary button in the pinned footer via ActivationFooter.
  if (isRedesignedActivationEnabled) {
    return (
      <ActivationFooterContext.Provider value={footerNode}>
        <ActivationScreen
          inlineHeader
          // The kit is already activated past this point, so there is no form
          // to return to: leaving means leaving the activation, warning first
          // when health forms are still pending.
          onBack={
            navigation.isAfterActivation
              ? hasHealthForms
                ? () => setIsExitModalOpen(true)
                : goHome
              : navigation.navigateBack
          }
          header={
            <ActivationStepper
              steps={navigation.activationSteps}
              currentStepKey={navigation.currentStepKey}
            />
          }
          footerNode={footerNode}
          scrollResetKey={currentActivationStep}
        >
          <div className={styles.redesignedSteps}>{stepElement}</div>
        </ActivationScreen>
        <ExitFormsWarningModal
          isOpen={isExitModalOpen}
          variant='landing'
          onCancel={() => setIsExitModalOpen(false)}
          onConfirm={goHome}
        />
      </ActivationFooterContext.Provider>
    );
  }

  return (
    <div className='max-w-100 p-3'>
      <ActivationNavigation
        step={currentActivationStep}
        startingStep={tinyAccount ? 1 : 0}
        steps={visibleSteps}
        setCurrentActivationStep={setCurrentActivationStep}
        kitId={activationState.kit.id}
        kit={activationState.kit}
      />
      {stepElement}
    </div>
  );
};
